import clsx from 'clsx'
import { CountUp } from '../ui/primitives'
import { TONE_BG, TONE_BORDER, TONE_TEXT, type Tone } from './tokens'

export interface KpiCardProps {
  label: string
  value: number | string
  unit?: string
  sub?: string
  tone?: Tone
  dot?: boolean
  /** Overrides tone's bg/border/dot with a literal hex, for states outside the shared
   *  five-tone vocabulary (e.g. EIM's charging teal and idle-alert blue -- console/src/eim/
   *  statusStyle.ts -- which needed colours the Tone enum doesn't have room for). `tone`
   *  still governs `sub`'s text colour, so pass 'neutral' alongside this for a plain sub. */
  accentColor?: string
  /** Optional — omitting it keeps every existing caller pixel- and behavior-identical
   *  (no role, no tabIndex, no cursor change). Set it to make the whole card a real
   *  button: keyboard-focusable, Enter/Space-activatable, `cursor-pointer`. */
  onClick?: () => void
}

const DOT_BG: Record<Tone, string> = {
  neutral: 'bg-[#94A3B8]',
  ok: 'bg-[#16A34A]',
  warn: 'bg-[#D97706]',
  danger: 'bg-[#DC2626]',
  info: 'bg-[#2563EB]',
}

const LONG_TEXT = 9

/** True for a string value too long to sit at the numeral size without wrapping. */
export function isLongText(value: number | string): value is string {
  return typeof value === 'string' && value.length > LONG_TEXT
}

export function KpiCard({ label, value, unit, sub, tone = 'neutral', dot, accentColor, onClick }: KpiCardProps) {
  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={onClick
        ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onClick()
            }
          }
        : undefined}
      className={clsx(
        'flex min-h-[112px] flex-col rounded-[14px] border p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)]',
        'transition-shadow duration-150 hover:shadow-[0_4px_12px_-4px_rgba(15,23,42,0.12)]',
        onClick && 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2563EB]',
        !accentColor && TONE_BG[tone],
        !accentColor && TONE_BORDER[tone],
      )}
      style={accentColor ? { backgroundColor: `${accentColor}1A`, borderColor: `${accentColor}42` } : undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-[#94A3B8]">
          {label}
        </span>
        {dot ? (
          <span
            className={clsx('mt-0.5 h-2 w-2 shrink-0 rounded-full', !accentColor && DOT_BG[tone])}
            style={accentColor ? { backgroundColor: accentColor } : undefined}
          />
        ) : null}
      </div>
      <div className="mt-2 flex min-w-0 items-baseline gap-1.5">
        {/* A word for a value ("Congestion cleared") at the 36px numeral size wraps the
            card to twice its row-mates' height; long text drops to a heading size and
            truncates, with the full value on hover. Numbers and short codes are unchanged. */}
        <span
          className={clsx(
            'font-mono font-bold leading-none tabular-nums text-[#0F172A]',
            isLongText(value) ? 'min-w-0 truncate py-1.5 text-[20px]' : 'text-[36px]',
          )}
          title={isLongText(value) ? value : undefined}
        >
          {typeof value === 'number' ? <CountUp value={value} /> : value ?? '—'}
        </span>
        {unit ? <span className="text-[13px] text-[#64748B]">{unit}</span> : null}
      </div>
      {sub ? (
        <div className={clsx('mt-auto pt-2 text-[12px] font-medium', tone === 'neutral' ? 'text-[#64748B]' : TONE_TEXT[tone])}>
          {sub}
        </div>
      ) : null}
    </div>
  )
}
